import * as React from 'react';
import Lazy from 'lazy.js';
import propz from 'propz';
import Moment from 'moment';
import { AppUser } from '../../../../../App/App';
import { History, Location } from 'history';
import { AGE_GROUPS } from '../../../../../../consts/school';
import { CLUB_EVENT_EDIT_MODE } from '../../../../../../consts/event';
import { TEAM_TYPE } from 'Src/consts/team';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from '../../../../../../consts/common';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, LIMIT } from '../../../../../../consts/table';
import { Loader } from '../../../../../../components/Loader/Loader';
import { TwoPanelEditor } from '../../../../../../components/TwoPanelEditor/TwoPanelEditor';
import { StatusModal } from 'Src/components/StatusModal/StatusModal';
import { SimpleModal } from '../../../../../../components/SimpleModal/SimpleModal';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { ClashEventsModal } from 'Src/components/ClashEventsModal/ClashEventsModal';
import { getAge, getEventType, getGender } from '../../../../../../helpers/accessor/accessor';
import {
  getEventClash,
  getClashEventDisplayTitle,
  getIndividualsMethod,
  getStudentsMethod,
  isHousesEvent,
  isClubEvent,
  isTournamentEvent
} from '../../../../../../helpers/event/event';
import {
  ColumnDefinition,
  getOrder,
  getServerFieldSectionWhere,
  isFilterExist2,
  TABLE_SORT_DIRECTION
} from '../../../../../../helpers/table/table';
import { getUserGenderArrayConvertedFromEventGender } from '../../../../../../helpers/tournament/tournament';
import { formatAgesCompact } from 'Src/helpers/ageGroups';
import { getFromHistory } from '../../../../../../helpers/history/history';
import { getTwoPanelEditorFilters } from '../../../../../../helpers/twoPanelEditor/twoPanelEditor';
import { getSchoolEventPlayersCount } from 'Src/helpers/service/admin/schoolEvents';
import { getAllClubParticipants } from 'Src/helpers/service/admin/clubParticipants';
import { addClubStudents } from 'Src/helpers/service/admin/clubStudents';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getSchoolEvent } from 'Src/helpers/service/admin/event';
import {
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForHouses
} from '../../../../../../helpers/table/select';
import { getSchoolTournamentTeams, addSchoolTournamentTeam } from 'Src/helpers/service/admin/tournamentTeams';
import { updateSchoolTournamentTeamPlayersBatch } from 'Src/helpers/service/admin/tournamentTeamPlayers';
import {
  getSchoolStudentsWithClashesCount,
  getSchoolStudentsWithoutClashes
} from '../../../../../../helpers/service/admin/schoolTeamPlayers';
import { getEventStartDateAndTime } from '../../../../../../helpers/event/schoolEventViewHelper';
import {
  updateSchoolEventIndividualsBatch,
  updateSchoolEventIndividualsGroupBatch
} from '../../../../../../helpers/service/admin/schoolEventIndividuals';
import { getColorClashes } from '../../../../../../helpers/cell/cell';
import { EventClash, SchoolEvent } from '../../../../../../models/event';
import { SchoolStudentClash } from '../../../../../../models/schoolStudent';
import { SchoolForm } from '../../../../../../models/form';
import { SchoolHouse } from '../../../../../../models/house';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import { SchoolEventIndividualWithClashes } from '../../../../../../models/eventIndividuals';
import { SchoolEventClubEventRadioButtons } from '../SchoolEventClubEventRadioButtons/SchoolEventClubEventRadioButtons';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface Item {
  id: string;
  permissionId: string;
  form: any;
  house: any;
  gender: string;
  firstName: string;
  lastName: string;
  clashes: {
    status: string;
    fullClashEvents: EventClash[];
    intervalClashEvents: EventClash[];
  };
  playerId?: string;
}

interface State {
  items: Item[];
  itemsSelected: Item[];
  itemsCount: number;

  itemsFilters: any;
  isShowItemsFilter: boolean;
  isItemsFiltered: boolean;

  isSelectAllItemsChecked: boolean;

  sortItemsDirection: TABLE_SORT_DIRECTION;
  sortItemsColumnsName: string;

  itemCurrentPage: number;

  itemsAccumulatorInitial: Item[];
  itemsAccumulator: Item[];
  itemsAccumulatorSelected: Item[];
  itemsAccumulatorCount: number;

  itemsAccumulatorFilters: any;
  isShowItemsAccumulatorFilter: boolean;
  isItemsAccumulatorFiltered: boolean;

  isSelectAllItemsAccumulatorChecked: boolean;

  sortItemsAccumulatorDirection: TABLE_SORT_DIRECTION;
  sortItemsAccumulatorColumnsName: string;

  isLoading: boolean;
  isItemsLoading: boolean;
  itemsSearch: string;

  itemsAddBuffer: Item[];
  itemsRemoveBuffer: Item[];

  forms: SchoolForm[];
  houses: SchoolHouse[];
  tournamentTeams: TournamentTeam[];

  event: SchoolEvent | undefined;
  editMode: string;
  isShowFollowingEventsSection: boolean;

  studentEventsModalText: string;
  isStudentEventsModalOpen: boolean;

  isHelpModalOpen: boolean;

  isMaxPlayersModalOpen: boolean;
  maxPlayersModalMessage: string;

  itemsHiddenFields: Set<string>;
  itemsAccumulatorHiddenFields: Set<string>;
}

const ITEMS_ACCUMULATOR_COLUMNS: ColumnDefinition[] = [
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge,
    isMobileHidden: true
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender,
    isMobileHidden: true
  }
];

const ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'Clashes',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge,
    isMobileHidden: true
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender,
    isMobileHidden: true
  }
];

const ITEMS_COLUMNS: ColumnDefinition[] = [
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge,
    isMobileHidden: true
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender,
    isMobileHidden: true
  }
];

const ITEMS_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'Clashes',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge,
    isMobileHidden: true
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender,
    isMobileHidden: true
  }
];

const DEFAULT_INDIVIDUAL_PLAYERS_ORDER = 'firstName ASC';

export class SchoolIndividualManager extends React.Component<Props, State> {
  private itemsSearchRequestId = 0;

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      itemsCount: 0,
      itemsSelected: [],

      itemsFilters: {},
      isShowItemsFilter: false,
      isItemsFiltered: false,

      isSelectAllItemsChecked: false,

      sortItemsDirection: '',
      sortItemsColumnsName: '',

      itemCurrentPage: 1,

      itemsAccumulatorInitial: [],
      itemsAccumulator: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],

      itemsAccumulatorFilters: {},
      isShowItemsAccumulatorFilter: false,
      isItemsAccumulatorFiltered: false,

      isSelectAllItemsAccumulatorChecked: false,

      sortItemsAccumulatorDirection: '',
      sortItemsAccumulatorColumnsName: '',

      isLoading: true,
      isItemsLoading: false,
      itemsSearch: '',

      itemsAddBuffer: [],
      itemsRemoveBuffer: [],

      forms: [],
      houses: [],
      event: undefined,
      tournamentTeams: [],
      studentEventsModalText: '',
      isStudentEventsModalOpen: false,

      isShowFollowingEventsSection: false,
      editMode: CLUB_EVENT_EDIT_MODE.SINGLE,
      isHelpModalOpen: false,

      isMaxPlayersModalOpen: false,
      maxPlayersModalMessage: '',

      itemsHiddenFields: this.loadHiddenFields('individualManager_items'),
      itemsAccumulatorHiddenFields: this.loadHiddenFields('individualManager_accumulator')
    };
  }

  private static ALWAYS_VISIBLE = new Set(['firstName', 'lastName', 'clashes']);

  private get storageUserId(): string {
    return this.props.user?.userId || 'anonymous';
  }

  private loadHiddenFields(key: string): Set<string> {
    const userId = this.props.user?.userId || 'anonymous';
    try {
      const raw = localStorage.getItem(`columnVisibility__${userId}__${key}`);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set<string>(parsed);
    } catch {}
    return new Set();
  }

  private saveHiddenFields(key: string, fields: Set<string>): void {
    try {
      localStorage.setItem(`columnVisibility__${this.storageUserId}__${key}`, JSON.stringify(Array.from(fields)));
    } catch {}
  }

  isColumnToggleable = (field: string): boolean => !SchoolIndividualManager.ALWAYS_VISIBLE.has(field);

  onToggleItemsColumn = (field: string): void => {
    if (SchoolIndividualManager.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('individualManager_items', next);
      return { itemsHiddenFields: next };
    });
  };

  onToggleItemsAccumulatorColumn = (field: string): void => {
    if (SchoolIndividualManager.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsAccumulatorHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('individualManager_accumulator', next);
      return { itemsAccumulatorHiddenFields: next };
    });
  };

  getDefaultItemsFilter(event: SchoolEvent) {
    const { gender, ages } = event;
    const genderArrayConverted = getUserGenderArrayConvertedFromEventGender(gender);
    const houseId = this.getHouseId();

    return isHousesEvent(event)
      ? {
          firstName: '',
          lastName: '',
          gender: genderArrayConverted,
          forms: [],
          ages: ages,
          houses: [houseId]
        }
      : {
          firstName: '',
          lastName: '',
          gender: genderArrayConverted,
          forms: [],
          ages: ages
        };
  }

  getDefaultItemsAccumulatorFilter(event: SchoolEvent) {
    const { gender, ages } = event;
    const genderArrayConverted = getUserGenderArrayConvertedFromEventGender(gender);
    const houseId = this.getHouseId();

    return isHousesEvent(event)
      ? {
          firstName: '',
          lastName: '',
          gender: genderArrayConverted,
          forms: [],
          ages: ages,
          houses: [houseId]
        }
      : {
          firstName: '',
          lastName: '',
          gender: genderArrayConverted,
          forms: [],
          ages: ages
        };
  }

  getHouseId(): string {
    const { history } = this.props;
    return getFromHistory(history, 'house');
  }

  getEventId(): string {
    const { history } = this.props;
    return getFromHistory(history, 'event');
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    const { user } = this.props;
    const eventId = this.getEventId();

    const itemsFilters = getTwoPanelEditorFilters(ITEMS_COLUMNS);
    const isShowItemsFilter = isFilterExist2(itemsFilters);
    const isItemsFiltered = isFilterExist2(itemsFilters);

    const itemsAccumulatorFilters = getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS);
    const isShowItemsAccumulatorFilter = isFilterExist2(itemsAccumulatorFilters);
    const isItemsAccumulatorFiltered = isFilterExist2(itemsAccumulatorFilters);

    const formsPromise = getAllForms(user);
    const housesPromise = getAllHouses(user);
    const eventPromise = getSchoolEvent(user, eventId);

    Promise.all([formsPromise, housesPromise, eventPromise])
      .then(([forms, houses, event]) => {
        const defaultItemsFilter = this.getDefaultItemsFilter(event);
        const defaultItemsAccumulatorFilter = this.getDefaultItemsAccumulatorFilter(event);

        this.setState({
          forms: forms,
          houses: houses,
          itemsFilters: defaultItemsFilter,
          itemsAccumulatorFilters: defaultItemsAccumulatorFilter,
          event: event
        });

        const tournamentTeamsPromise = isTournamentEvent(event)
          ? getSchoolTournamentTeams(user, event.tournamentId)
          : Promise.resolve(undefined);

        return Promise.all([this.getItemsAndItemsAccumulator(event), tournamentTeamsPromise]);
      })
      .then(([{ playersInitial, players, playersCount, students, studentsCount }, tournamentTeams]) => {
        const studentsNormalized = this.getStudentsNormalized(students);
        const playersNormalized = this.getPlayersNormalized(players);
        const playersInitialNormalized = this.getPlayersNormalized(playersInitial);

        this.setState({
          items: studentsNormalized,
          itemsCount: studentsCount.count,
          isShowItemsFilter: isShowItemsFilter,
          isItemsFiltered: isItemsFiltered,

          itemsAccumulatorInitial: playersInitialNormalized,
          itemsAccumulator: playersNormalized,
          itemsAccumulatorCount: playersCount.count,
          isShowItemsAccumulatorFilter: isShowItemsAccumulatorFilter,
          isItemsAccumulatorFiltered: isItemsAccumulatorFiltered,
          tournamentTeams,

          isLoading: false
        });
      });
  }

  getPlayersNormalized(players: SchoolEventIndividualWithClashes[]): Item[] {
    return players.map(player => {
      const { permissionId, form, gender, firstName, lastName, userId, id, house, clashes } = player;
      return {
        permissionId,
        form,
        house,
        gender: gender ?? '',
        firstName,
        lastName,
        clashes,
        id: userId,
        playerId: id
      };
    });
  }

  getStudentsNormalized(students: SchoolStudentClash[]): Item[] {
    return students.map(student => {
      const { permissionId, form, gender, firstName, lastName, id, house, clashes } = student;

      return {
        permissionId,
        form,
        house,
        gender: gender ?? '',
        firstName,
        lastName,
        clashes,
        id
      };
    });
  }

  getItemsFilter(page, itemsAccumulator, itemsAddBuffer, isAllItems = false) {
    const { itemsFilters, forms, event, itemsSearch } = this.state;

    if (!event) return null;

    const { startTime, endTime, players, id } = event;

    const {
      gender: itemsFiltersGender,
      ages: itemsFiltersAges = [],
      firstName: itemsFiltersFirstName = '',
      lastName: itemsFiltersLastName = '',
      forms: itemsFiltersForms = [],
      houses: itemsFiltersHouses = []
    } = itemsFilters;

    let where: any = {
      eventId: id,
      gender: {
        $in: itemsFiltersGender
      }
    };

    const playerUserIds = players
      .filter(player => {
        if (isHousesEvent(event)) {
          const houseId = this.getHouseId();
          return player.houseId !== houseId;
        } else {
          return false;
        }
      })
      .map(player => ({
        id: player.userId
      }));

    const allItems = [...itemsAccumulator, ...itemsAddBuffer, ...playerUserIds];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);

    if (itemsFiltersForms.length > 0) {
      propz.set(where, ['formId', '$in'], itemsFiltersForms);
    } else {
      const ages = itemsFiltersAges.length > 0 ? itemsFiltersAges : AGE_GROUPS.ENGLISH.map((age, index) => index);
      const formIds = forms.filter(form => ages.some(age => Number(form.age) === Number(age))).map(form => form.id);

      if (formIds.length > 0) {
        propz.set(where, ['formId', '$in'], formIds);
      }
    }

    if (isHousesEvent(event)) {
      const houseId = this.getHouseId();
      propz.set(where, ['houseId'], houseId);
    }

    if (itemsFiltersHouses.length > 0) {
      propz.set(where, ['houseId', '$in'], itemsFiltersHouses);
    }

    let and: any[] = [];

    if (itemsFiltersFirstName.length > 0) {
      and.push({ firstName: { like: itemsFiltersFirstName, options: 'i' } });
    }

    if (itemsFiltersLastName.length > 0) {
      and.push({ lastName: { like: itemsFiltersLastName, options: 'i' } });
    }

    if (itemsSearch.trim().length > 0) {
      const searchValue = itemsSearch.trim();
      and.push({
        $or: [{ firstName: { like: searchValue, options: 'i' } }, { lastName: { like: searchValue, options: 'i' } }]
      });
    }

    if (and.length > 0) {
      propz.set(where, ['$and'], and);
    }

    const startTimeDate = new Date(startTime);
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = Moment(startTimeCopy)
      .utc()
      .format();

    if (!startTime || !endTime) return;

    const endTimeDate = new Date(endTime);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = Moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    const filter = {
      where: where,
      limit: isAllItems ? DEFAULT_LIMIT : LIMIT,
      skip: isAllItems ? 0 : (page - 1) * LIMIT,
      order: DEFAULT_INDIVIDUAL_PLAYERS_ORDER
    };

    return filter;
  }

  getItemsAndItemsAccumulator(event: SchoolEvent) {
    const { user } = this.props;
    const { itemsAccumulatorFilters, forms } = this.state;

    const options = { forms };
    const where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);

    const eventId = this.getEventId();

    if (isHousesEvent(event)) {
      const houseId = this.getHouseId();
      propz.set(where, ['houseId', '$in'], [houseId]);
    }

    const getPlayersPromise = getIndividualsMethod(user)(user, eventId, { where });
    const getPlayersCountPromise = getSchoolEventPlayersCount(user, eventId, where);

    let playersInitial, players, playersCount;

    return Promise.all([getPlayersPromise, getPlayersCountPromise])
      .then(([_players, _playersCount]) => {
        playersInitial = _players;
        players = _players;
        playersCount = _playersCount;

        const playersInitialNormalized = this.getPlayersNormalized(playersInitial);
        const filter = this.getItemsFilter(1, playersInitialNormalized, []);

        const method = getStudentsMethod(user);
        const getStudentsPromise = method(user, filter);
        const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

        return Promise.all([getStudentsPromise, getStudentsCountPromise]);
      })
      .then(([students, studentsCount]) => {
        return {
          playersInitial,
          players,
          playersCount,
          students,
          studentsCount
        };
      });
  }

  onItemClick = (index: number): void => {
    const { items, itemsSelected } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = itemsSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: [],
      itemsSelected: selectedItemsUpdated
    });
  };

  onItemAccumulatorClick = (index: number): void => {
    const { itemsAccumulator, itemsAccumulatorSelected } = this.state;
    const selectedItem = itemsAccumulator[index];

    const selectedItemIndex = itemsAccumulatorSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsAccumulatorSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: selectedItemsUpdated,
      itemsSelected: []
    });
  };

  onAddClick = () => {
    const {
      itemsSelected,
      itemsAddBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage,
      event
    } = this.state;
    const { user } = this.props;

    if (event && isTournamentEvent(event)) {
      const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
      if (typeof maxPlayers === 'number' && maxPlayers > 0) {
        const currentCount = itemsAccumulator.length;
        const tryingToAdd = itemsSelected.length;
        if (currentCount + tryingToAdd > maxPlayers) {
          this.setState({
            isMaxPlayersModalOpen: true,
            maxPlayersModalMessage: `Player limit reached: you can have maximum ${maxPlayers} participant(s) per school. Currently ${currentCount} selected, trying to add ${tryingToAdd}.`
          });
          return;
        }
      }
    }

    const nextItemsAddBuffer = [...itemsAddBuffer, ...itemsSelected];

    const existingIds = new Set((itemsAccumulator || []).map(x => String(x.id)));
    const nextItemsAccumulator = [...itemsAccumulator, ...itemsSelected.filter(x => !existingIds.has(String(x.id)))];

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAddBuffer: nextItemsAddBuffer,
        itemsAccumulator: nextItemsAccumulator,
        itemsAccumulatorCount: nextItemsAccumulator.length,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isSelectAllItemsChecked: false
      });
    });
  };

  onRemoveClick = () => {
    const {
      itemsAccumulatorSelected,
      itemsAddBuffer,
      itemsRemoveBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage
    } = this.state;
    const { user } = this.props;

    const selectedIds = new Set((itemsAccumulatorSelected || []).map(x => String(x.id)));
    const addBufferIds = new Set((itemsAddBuffer || []).map(x => String(x.id)));

    const selectedExistingParticipants = (itemsAccumulatorSelected || []).filter(x => !addBufferIds.has(String(x.id)));

    const nextItemsAddBuffer = (itemsAddBuffer || []).filter(x => !selectedIds.has(String(x.id)));

    const existingRemoveIds = new Set((itemsRemoveBuffer || []).map(x => String(x.id)));
    const nextItemsRemoveBuffer = [
      ...(itemsRemoveBuffer || []),
      ...selectedExistingParticipants.filter(x => !existingRemoveIds.has(String(x.id)))
    ];

    const nextItemsAccumulatorInitial = (itemsAccumulatorInitial || []).filter(
      itemAccumulatorInitial =>
        !nextItemsRemoveBuffer.some(
          nextItemRemoveBuffer => String(nextItemRemoveBuffer.id) === String(itemAccumulatorInitial.id)
        )
    );

    const nextItemsAccumulator = (itemsAccumulator || []).filter(
      itemAccumulator => !selectedIds.has(String(itemAccumulator.id))
    );

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(itemCurrentPage, nextItemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isSelectAllItemsChecked: false,
        itemsAddBuffer: nextItemsAddBuffer,
        itemsRemoveBuffer: nextItemsRemoveBuffer,
        itemsAccumulatorInitial: nextItemsAccumulatorInitial,
        itemsAccumulator: nextItemsAccumulator,
        itemsAccumulatorCount: nextItemsAccumulator.length
      });
    });
  };

  onClickSave = () => {
    const {
      itemsAddBuffer,
      itemsRemoveBuffer,
      editMode,
      isShowFollowingEventsSection,
      itemsAccumulator,
      event,
      tournamentTeams
    } = this.state;

    if (!event) return null;

    const { clubId } = event;
    const { user, history } = this.props;
    const { activeSchoolId } = user;

    const groupEditMode = editMode === CLUB_EVENT_EDIT_MODE.GROUP;
    const eventId = this.getEventId();

    switch (true) {
      case isClubEvent(event) && !isShowFollowingEventsSection:
        this.showFollowingEventsSection();

        break;

      case isClubEvent(event) && groupEditMode:
        {
          this.setState({
            isLoading: true
          });

          const playersCommit = itemsAccumulator.map(player => {
            return {
              userId: player.id,
              permissionId: player.permissionId
            };
          });

          const teamPromise =
            isClubEvent(event) && clubId
              ? Promise.all([
                  getAllClubParticipants(user, clubId),
                  updateSchoolEventIndividualsGroupBatch(user, eventId, { participants: playersCommit })
                ])
              : updateSchoolEventIndividualsGroupBatch(user, eventId, { participants: playersCommit });

          teamPromise
            .then(response => {
              if (isClubEvent(event) && clubId) {
                const [participants] = response;

                const studentsAreNotClubParticipants = itemsAddBuffer.filter(item => {
                  return !participants.some(participant => {
                    return participant.userId === item.id;
                  });
                });

                const isStudentsAreNotClubParticipantsExist = studentsAreNotClubParticipants.length > 0;

                if (isStudentsAreNotClubParticipantsExist) {
                  const studentToAddData = {
                    syncUpcomingEvents: false,
                    participants: studentsAreNotClubParticipants.map(student => {
                      return {
                        userId: student.id,
                        permissionId: student.permissionId
                      };
                    })
                  };

                  return addClubStudents(user, clubId, studentToAddData);
                }
              }
            })
            .then(() => {
              history.push({
                pathname: '/events/event',
                search: `id=${eventId}`
              });
            });
        }

        break;

      case isTournamentEvent(event):
        const { tournamentId } = event;

        const currentSchoolTeam = tournamentTeams.find(team => team.schoolId === activeSchoolId);
        const isCurrentSchoolTeamExist = typeof currentSchoolTeam !== 'undefined';

        const tournamentTeamsPlayers = propz.get(currentSchoolTeam, ['players'], []);
        const tournamentTeamsPlayersIds = tournamentTeamsPlayers.map(player => player.userId);

        const studentsToAddToTeam = itemsAddBuffer.filter(item => !tournamentTeamsPlayersIds.includes(item.id));
        const isStudentsToAddToTeamExist = studentsToAddToTeam.length > 0;

        this.setState({
          isLoading: true
        });

        const data = {
          add: itemsAddBuffer.map(item => ({ userId: item.id, permissionId: item.permissionId })),
          remove: itemsRemoveBuffer.map(item => item.playerId).filter((id): id is string => typeof id === 'string')
        };

        updateSchoolEventIndividualsBatch(user, eventId, data)
          .then(() => {
            switch (true) {
              case isCurrentSchoolTeamExist:
                if (isStudentsToAddToTeamExist) {
                  const addToTeamData = {
                    add: studentsToAddToTeam.map(item => ({ userId: item.id, permissionId: item.permissionId })),
                    remove: []
                  };

                  return updateSchoolTournamentTeamPlayersBatch(
                    user,
                    tournamentId,
                    currentSchoolTeam.id,
                    addToTeamData
                  );
                }
                break;

              case !isCurrentSchoolTeamExist:
                return this.createDefaultTeam();
            }
          })
          .then(newTeam => {
            if (!isCurrentSchoolTeamExist && tournamentId) {
              const addToTeamData = {
                add: studentsToAddToTeam.map(item => ({ userId: item.id, permissionId: item.permissionId })),
                remove: []
              };

              return updateSchoolTournamentTeamPlayersBatch(user, tournamentId, newTeam.id, addToTeamData);
            }
          })
          .then(() => {
            history.push({
              pathname: '/events/event',
              search: `id=${eventId}`
            });
          });
        break;

      default:
        {
          this.setState({
            isLoading: true
          });

          const data = {
            add: itemsAddBuffer.map(item => ({ userId: item.id, permissionId: item.permissionId })),
            remove: itemsRemoveBuffer.map(item => item.playerId).filter((id): id is string => typeof id === 'string')
          };

          const teamPromise =
            isClubEvent(event) && clubId
              ? Promise.all([
                  getAllClubParticipants(user, clubId),
                  updateSchoolEventIndividualsBatch(user, eventId, data)
                ])
              : updateSchoolEventIndividualsBatch(user, eventId, data);

          teamPromise
            .then(response => {
              if (isClubEvent(event)) {
                const [participants] = response;

                const studentsAreNotClubParticipants = itemsAddBuffer.filter(item => {
                  return !participants.some(participant => {
                    return participant.userId === item.id;
                  });
                });

                const isStudentsAreNotClubParticipantsExist = studentsAreNotClubParticipants.length > 0;

                if (isStudentsAreNotClubParticipantsExist && clubId) {
                  const studentToAddData = {
                    syncUpcomingEvents: false,
                    participants: studentsAreNotClubParticipants.map(student => {
                      return {
                        userId: student.id,
                        permissionId: student.permissionId
                      };
                    })
                  };

                  return addClubStudents(user, clubId, studentToAddData);
                }
              }
            })
            .then(() => {
              history.push({
                pathname: '/events/event',
                search: `id=${eventId}`
              });
            });
        }

        break;
    }
  };

  createDefaultTeam = () => {
    const { user } = this.props;
    const { event } = this.state;

    if (!event) return Promise.reject('No event');

    const { activeSchoolId: schoolId } = user;
    const { tournament, tournamentId } = event as any;
    const { gender, ages, sportId } = tournament;
    const tournamentName = propz.get(tournament, ['name'], '');
    const schoolName = propz.get(user, ['activeSchoolName'], '');
    const name = `${schoolName} ${tournamentName}`;

    const teamData = {
      name,
      ages,
      gender,
      sportId,
      schoolId,
      teamType: TEAM_TYPE.ADHOC,
      isAutoEnrolledTeam: true,
      players: []
    };

    return addSchoolTournamentTeam(user, tournamentId, teamData);
  };

  onItemsFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (filterField === 'forms') {
      nextFilters.ages = [];
    }

    if (filterField === 'ages') {
      nextFilters.forms = [];
    }

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      if (!filter) return;
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsFilters: nextFilters
    });
  };

  onItemsAccumulatorFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsAccumulatorFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      if (!filter) return;
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsAccumulatorFilters: nextFilters
    });
  };

  onItemsFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsFilter } = this.state;

    this.setState({
      isShowItemsFilter: !isShowItemsFilter
    });
  };

  onItemsAccumulatorFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsAccumulatorFilter } = this.state;

    this.setState({
      isShowItemsAccumulatorFilter: !isShowItemsAccumulatorFilter
    });
  };

  itemsSetCurrentPageParams = (currentPage: number): void => {
    const { user } = this.props;
    const { itemsAccumulator, itemsAddBuffer } = this.state;

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(currentPage, itemsAccumulator, itemsAddBuffer);

    const method = getStudentsMethod(user);

    method(user, filter).then(students => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        itemCurrentPage: currentPage,
        items: studentsNormalized,
        isLoading: false
      });
    });
  };

  itemsOnApplyFilterClick = (isSilent = false) => {
    const { itemsAccumulator, itemsAddBuffer } = this.state;
    const requestId = ++this.itemsSearchRequestId;

    if (isSilent) {
      this.setState({ isItemsLoading: true });
    } else {
      this.setState({ isLoading: true });
    }

    const filter = this.getItemsFilter(1, itemsAccumulator, itemsAddBuffer);

    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentCountPromise]).then(([students, studentsCount]) => {
      if (requestId !== this.itemsSearchRequestId) return;
      const studentsNormalized = this.getStudentsNormalized(students);

      const nextState = {
        items: studentsNormalized,
        isLoading: isSilent ? this.state.isLoading : false,
        isItemsLoading: false,
        itemsCount: studentsCount.count,
        itemCurrentPage: 1,
        sortItemsDirection: '' as TABLE_SORT_DIRECTION,
        sortItemsColumnsName: ''
      };

      if (isSilent) {
        this.setState(nextState);
      } else {
        this.setState({ ...nextState, itemsAccumulatorSelected: [], itemsSelected: [] });
      }
    });
  };

  onItemsSearchChange = (itemsSearch: string): void => {
    this.setState({ itemsSearch, isItemsLoading: true }, () => this.itemsOnApplyFilterClick(true));
  };

  itemsAccumulatorOnApplyFilterClick = () => {};
  // itemsAccumulatorOnApplyFilterClick = () => {
  //   const { itemsAccumulatorFilters, itemsRemoveBuffer, forms } = this.state;
  //   this.setState({
  //     isLoading: true
  //   });
  //
  //   const options = { forms };
  //
  //   let where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);
  //   const ninIds = itemsRemoveBuffer.map(item => item.id);
  //   propz.set(where, ['id', '$nin'], ninIds);
  //
  //   const { user } = this.props;
  //
  //   const eventId = this.getEventId();
  //
  //   const getPlayersPromise = getAllSchoolEventIndividuals(user, eventId, where);
  //   const getPlayersCountPromise = getSchoolEventPlayersCount(user, eventId, where);
  //
  //   const promiseItems = [getPlayersPromise, getPlayersCountPromise];
  //
  //   BPromise.all(promiseItems).then(([players, playersCount]) => {
  //     const playersNormalized = this.getPlayersNormalized(players);
  //
  //     this.setState({
  //       itemsAccumulator: playersNormalized,
  //       isLoading: false,
  //       itemsAccumulatorCount: playersCount.count,
  //       itemsAccumulatorSelected: [],
  //       itemsSelected: []
  //     });
  //   });
  // };

  itemsOnClearFilterClick = () => {
    const { itemsAccumulatorInitial, itemsAddBuffer, forms, event } = this.state;

    if (!event) return null;

    const defaultItemsFilter = this.getDefaultItemsFilter(event);

    this.setState({
      isLoading: true,
      itemsFilters: defaultItemsFilter
    });

    const { user } = this.props;
    const { startTime, endTime, players, id } = event;
    if (!startTime || !endTime) return;

    const {
      gender: itemsFiltersGender,
      ages: itemsFiltersAges = [],
      firstName: itemsFiltersFirstName = '',
      lastName: itemsFiltersLastName = ''
    } = defaultItemsFilter;

    let where: any = {
      eventId: id,
      gender: {
        $in: itemsFiltersGender
      }
    };

    const playerUserIds = players
      .filter(player => {
        if (isHousesEvent(event)) {
          const houseId = this.getHouseId();
          return player.houseId !== houseId;
        } else {
          return false;
        }
      })
      .map(player => ({
        id: player.userId
      }));

    const allItems = [...itemsAccumulatorInitial, ...itemsAddBuffer, ...playerUserIds];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);

    const ages = itemsFiltersAges.length > 0 ? itemsFiltersAges : AGE_GROUPS.ENGLISH.map((age, index) => index);
    const formIds = forms.filter(form => ages.some(age => Number(form.age) === Number(age))).map(form => form.id);

    if (formIds.length > 0) {
      propz.set(where, ['formId', '$in'], formIds);
    }

    if (isHousesEvent(event)) {
      const houseId = this.getHouseId();
      propz.set(where, ['houseId'], houseId);
    }

    let and: any[] = [];

    if (itemsFiltersFirstName.length > 0) {
      and.push({ firstName: { like: itemsFiltersFirstName, options: 'i' } });
    }

    if (itemsFiltersLastName.length > 0) {
      and.push({ lastName: { like: itemsFiltersLastName, options: 'i' } });
    }

    if (and.length > 0) {
      propz.set(where, ['$and'], and);
    }

    const startTimeDate = new Date(startTime);
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = Moment(startTimeCopy)
      .utc()
      .format();

    const endTimeDate = new Date(endTime);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = Moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    const filter = {
      where: where,
      limit: LIMIT,
      skip: DEFAULT_SKIP,
      order: DEFAULT_INDIVIDUAL_PLAYERS_ORDER
    };

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsColumnsName: '',
        sortItemsDirection: ''
      });
    });
  };

  itemsAccumulatorOnClearFilterClick = () => {};
  // itemsAccumulatorOnClearFilterClick = () => {
  //   this.setState({
  //     isLoading: true
  //   });
  //
  //   const { user } = this.props;
  //   const { itemsRemoveBuffer } = this.state;
  //
  //   const eventId = this.getEventId();
  //
  //   const getPlayersPromise = getAllSchoolEventIndividuals(user, eventId);
  //   const getPlayersCountPromise = getSchoolEventPlayersCount(user, eventId);
  //
  //   const promiseItems = [getPlayersPromise, getPlayersCountPromise];
  //
  //   BPromise.all(promiseItems).then(([players, playersCount]) => {
  //     const playersNormalized = this.getPlayersNormalized(players);
  //     const nextPlayersNormalized = playersNormalized.filter(playerNormalized =>
  //       itemsRemoveBuffer.every(item => item.id !== playerNormalized.id)
  //     );
  //
  //     this.setState({
  //       itemsAccumulator: nextPlayersNormalized,
  //       isLoading: false,
  //       itemsAccumulatorCount: playersCount.count,
  //       itemsAccumulatorSelected: [],
  //       itemsSelected: [],
  //       itemsAccumulatorFilters: {}
  //     });
  //   });
  // };

  onSelectAllItemsClick = () => {
    const { itemsSelected, itemCurrentPage, itemsAccumulator, itemsAddBuffer } = this.state;

    this.setState({
      isLoading: true
    });

    const { user } = this.props;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer, true);

    getSchoolStudentsWithoutClashes(user, filter).then(items => {
      const itemsFiltered = items.filter(item => {
        return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
      });

      const studentsNormalizedFiltered = this.getStudentsNormalized(itemsFiltered);

      const nextSelectedItems = [...itemsSelected, ...studentsNormalizedFiltered];

      this.setState({
        itemsSelected: nextSelectedItems,
        isSelectAllItemsChecked: true,
        itemsAccumulatorSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  onSelectAllItemsOnPageClick = () => {
    const { items, itemsSelected } = this.state;

    const itemsFiltered = items.filter(item => {
      return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsSelected, ...itemsFiltered];

    this.setState({
      itemsSelected: nextSelectedItems,
      isSelectAllItemsChecked: true,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onUnselectAllItemsClick = () => {
    this.setState({
      itemsSelected: [],
      isSelectAllItemsChecked: false,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onSelectAllItemsAccumulatorClick = () => {
    const { itemsAccumulatorSelected, itemsAccumulatorFilters, itemsRemoveBuffer, forms, event } = this.state;
    if (!event) return null;

    this.setState({
      isLoading: true
    });

    const { user } = this.props;

    const eventId = this.getEventId();
    const options = { forms };
    const where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);

    if (isHousesEvent(event)) {
      const houseId = this.getHouseId();
      propz.set(where, ['houseId', '$in'], [houseId]);
    }

    getIndividualsMethod(user)(user, eventId, { where }).then(players => {
      const playersNormalized = this.getPlayersNormalized(players);

      const playersFiltered = playersNormalized.filter(player => {
        return itemsAccumulatorSelected.every(selectedItem => selectedItem.id !== player.id);
      });

      const nextSelectedItems = [...itemsAccumulatorSelected, ...playersFiltered].filter(item => {
        return itemsRemoveBuffer.every(itemRemoveBuffer => itemRemoveBuffer.id !== item.id);
      });

      this.setState({
        itemsAccumulatorSelected: nextSelectedItems,
        isSelectAllItemsAccumulatorChecked: true,
        itemsSelected: [],
        isSelectAllItemsChecked: false,
        isLoading: false
      });
    });
  };

  onSelectAllItemsAccumulatorOnPageClick = () => {
    const { itemsAccumulator, itemsAccumulatorSelected, itemsRemoveBuffer } = this.state;

    const itemsFiltered = itemsAccumulator.filter(item => {
      return itemsAccumulatorSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsAccumulatorSelected, ...itemsFiltered].filter(item => {
      return itemsRemoveBuffer.every(itemRemoveBuffer => itemRemoveBuffer.id !== item.id);
    });

    this.setState({
      itemsAccumulatorSelected: nextSelectedItems,
      isSelectAllItemsAccumulatorChecked: true,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  onUnselectAllItemsAccumulatorClick = () => {
    this.setState({
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  goBack = () => {
    const { event } = this.state;
    const { history } = this.props;

    if (!event) return null;

    const eventId = event.id;
    history.push({
      pathname: '/events/event',
      search: `id=${eventId}`
    });
  };

  onSortItemsClick = (sortField: string) => {
    const { sortItemsDirection, sortItemsColumnsName, itemCurrentPage, itemsAccumulator, itemsAddBuffer } = this.state;

    const order = getOrder(sortField, sortItemsDirection, sortItemsColumnsName) as TABLE_SORT_DIRECTION;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer);
    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsColumnsName: sortField,
        sortItemsDirection: order
      });
    });
  };

  onSortItemsAccumulatorClick = (sortField: string) => {};

  onClashClick = (student: SchoolStudentClash) => {
    const { user } = this.props;
    const { clashes, firstName, lastName } = student;
    const { fullClashEvents, intervalClashEvents } = clashes;
    const fullClashEventIds = fullClashEvents.map(fullClashEvent => fullClashEvent._id);
    const intervalClashEventIds = intervalClashEvents.map(intervalClashEvent => intervalClashEvent._id);

    const allPlayerEventIds = [...fullClashEventIds, ...intervalClashEventIds];
    const allPlayerEventIdsUniq = Lazy(allPlayerEventIds)
      .uniq()
      .toArray();

    this.setState({
      isLoading: true
    });

    const eventsPromise: Promise<SchoolEvent>[] = allPlayerEventIdsUniq.map(eventId => getSchoolEvent(user, eventId));

    Promise.all(eventsPromise).then(events => {
      const eventNames = events
        .map((event, index) => {
          const title = getClashEventDisplayTitle(event, user);
          const clash = getEventClash(event, fullClashEventIds, intervalClashEventIds);
          return `${index + 1}. ${title} ${clash}`;
        })
        .join('\n ');

      this.setState({
        isLoading: false,
        studentEventsModalText: `${firstName} ${lastName} is participating in the following events: \n${eventNames}`,
        isStudentEventsModalOpen: true
      });
    });
  };

  onCloseStudentEventsModalClick = () => {
    this.setState({
      isStudentEventsModalOpen: false
    });
  };

  onCloseMaxPlayersModal = () => {
    this.setState({ isMaxPlayersModalOpen: false, maxPlayersModalMessage: '' });
  };

  renderStudentEventsModal(): React.ReactNode {
    const { isStudentEventsModalOpen, studentEventsModalText } = this.state;
    return (
      <ClashEventsModal
        isOpen={isStudentEventsModalOpen}
        text={studentEventsModalText}
        onClose={this.onCloseStudentEventsModalClick}
      />
    );
  }

  renderMaxPlayersModal(): React.ReactNode {
    const { isMaxPlayersModalOpen, maxPlayersModalMessage } = this.state;
    return (
      <StatusModal
        isOpen={isMaxPlayersModalOpen}
        status="warning"
        title="Player limit reached"
        message={maxPlayersModalMessage}
        onClose={this.onCloseMaxPlayersModal}
      />
    );
  }

  showFollowingEventsSection = () => {
    this.setState({
      isShowFollowingEventsSection: true
    });
  };

  onEditModeClick = editMode => {
    this.setState({
      editMode: editMode
    });
  };

  openHelpModal = () => {
    this.setState({
      isHelpModalOpen: true
    });
  };

  closeHelpModal = () => {
    this.setState({
      isHelpModalOpen: false
    });
  };

  renderHelpModal(): React.ReactNode {
    const { isHelpModalOpen, event } = this.state;

    const maxPlayers = event ? propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']) : null;
    const hasLimit = event && isTournamentEvent(event) && typeof maxPlayers === 'number' && maxPlayers > 0;

    return (
      <HelpModal
        isOpen={isHelpModalOpen}
        title="Manage event participants"
        badgeText="Step 2"
        subtitle="Review eligible students, move participants into the event and save your changes when you are ready."
        onClose={() => this.setState({ isHelpModalOpen: false })}
      >
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">What you are seeing</div>
              <div className="text-muted">
                At the top of the page you can see the event date, sport, gender and age group. This is a quick check
                that you are editing the correct event before changing participants.
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Eligible students</div>
              <div className="text-muted">
                The right-hand panel shows students who match the event criteria. You can narrow the list using filters
                such as name, form, house, age and gender.
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Participants</div>
              <div className="text-muted">
                Move students into the participants panel to prepare the event list. These changes stay staged on screen
                until you click Save.
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Checking clashes</div>
              <div className="text-muted">
                If your school has clash visibility enabled, you can inspect possible clashes before saving and review
                other events a student is already involved in.
              </div>
            </div>
          </div>

          {hasLimit && (
            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Participant limit</div>
                <div className="text-muted">
                  This sport has a maximum of <strong>{maxPlayers}</strong> participants per school. You will not be
                  able to add more than this number to the event.
                </div>
              </div>
            </div>
          )}

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Club event updates</div>
              <div className="text-muted">
                For club events, you may be asked whether changes should apply only to this event or also to following
                events. Choose the option that matches what you want to update.
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Before you finish</div>
              <div className="text-muted">
                Use Cancel if you want to leave without applying staged changes. Use Save when the participant list
                looks right and you want to commit the update.
              </div>
            </div>
          </div>
        </div>
      </HelpModal>
    );
  }

  render() {
    const {
      items,
      isLoading,
      itemsSelected,
      itemsAccumulatorSelected,
      itemsAccumulator,
      itemsFilters,
      isShowItemsFilter,
      isItemsFiltered,
      itemsCount,
      itemsAccumulatorCount,
      itemCurrentPage,
      itemsAccumulatorFilters,
      isShowItemsAccumulatorFilter,
      isItemsAccumulatorFiltered,
      isSelectAllItemsChecked,
      isSelectAllItemsAccumulatorChecked,
      sortItemsDirection,
      sortItemsColumnsName,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName,
      itemsAddBuffer,
      itemsRemoveBuffer,
      forms,
      houses,
      event,
      isStudentEventsModalOpen,
      editMode,
      isShowFollowingEventsSection,
      itemsHiddenFields,
      itemsAccumulatorHiddenFields
    } = this.state;

    if (!event) return null;

    if (isLoading) {
      return <Loader />;
    }

    const { user } = this.props;
    const { activeSchool } = user;
    const { ageGroupsNaming, isShowStudentEventClashes } = activeSchool;

    const allItemsColumns = isShowStudentEventClashes ? ITEMS_COLUMNS_WITH_CLASHES : ITEMS_COLUMNS;
    const allAccumulatorColumns = isShowStudentEventClashes
      ? ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES
      : ITEMS_ACCUMULATOR_COLUMNS;
    const visibleItemsColumns = allItemsColumns.filter(col => !itemsHiddenFields.has(col.field));
    const visibleAccumulatorColumns = allAccumulatorColumns.filter(col => !itemsAccumulatorHiddenFields.has(col.field));

    const eventStartDateAndTimeFormatted = getEventStartDateAndTime(event);
    const eventSportName = propz.get(event, ['sport', 'name'], '');
    const eventGender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender];
    const eventStatus = getEventType(event);
    const eventAgesCompact = formatAgesCompact(event.ages, ageGroupsNaming) || 'All ages';
    const eventSummaryTitle = [eventSportName, eventGender, eventAgesCompact].filter(Boolean).join(' · ');
    const isShowSetupCard = isHousesEvent(event);

    const itemsFiltersOptions = {
      ages: getSelectOptionForAge(forms, ageGroupsNaming),
      gender: getSelectOptionForGender(),
      forms: getSelectOptionForForms(forms),
      houses: getSelectOptionForHouses(houses)
    };

    const itemsAccumulatorFiltersOptions = {};

    const houseId = this.getHouseId();
    const house = event.housesData.find(eventHouse => eventHouse.id === houseId);

    const classes = isStudentEventsModalOpen ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {this.renderStudentEventsModal()}
        {this.renderHelpModal()}
        {this.renderMaxPlayersModal()}
        <div className="bSchoolTeamManager__header">
          <div className="bSchoolTeamManager__summaryCard">
            <div className="bSchoolTeamManager__summaryMain">
              <div className="bSchoolTeamManager__summaryDate">{eventStartDateAndTimeFormatted}</div>
              <div className="bSchoolTeamManager__summaryTitle">{eventSummaryTitle}</div>
              <div className="bSchoolTeamManager__summaryText">
                Update the participants for this event. Changes are staged here and applied when you save.
              </div>
              {event &&
                isTournamentEvent(event) &&
                (() => {
                  const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
                  const hasLimit = typeof maxPlayers === 'number' && maxPlayers > 0;
                  return hasLimit ? (
                    <div className="bSchoolTeamManager__summaryText mt-1" style={{ color: '#f0ad4e', fontWeight: 600 }}>
                      Max {maxPlayers} participants per school
                    </div>
                  ) : null;
                })()}
            </div>

            <HelpIconButton
              onClick={() => this.setState({ isHelpModalOpen: true })}
              label="How to manage individual participants"
            />
          </div>

          {isShowSetupCard && house && (
            <div className="bSchoolTeamManager__setupCard">
              <div className="bSchoolTeamManager__fieldGroup">
                <label className="bSchoolTeamManager__label">House name</label>
                <select
                  className="form-control bSchoolTeamManager__input bSchoolTeamManager__input--compact"
                  disabled
                  value={houseId}
                >
                  <option key={house.id} value={house.id}>
                    {house.name}
                  </option>
                </select>
              </div>
            </div>
          )}
        </div>

        <section className="bSchoolTeamManager__participantsCard">
          <div className="bSchoolTeamManager__participantsHead">
            <span className="bSchoolTeamManager__participantsIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6M22 11h-6" />
              </svg>
            </span>
            <div>
              <h2 className="bSchoolTeamManager__participantsTitle">Select participants</h2>
              <div className="bSchoolTeamManager__participantsSubtitle">
                Choose eligible students on the left and review the event participants on the right.
              </div>
            </div>
          </div>
          <div className="bSchoolTeamManager__participantsDivider" />
          <div className="bSchoolTeamManager__editorWrap">
            <TwoPanelEditor
              isSourceFirst
              onItemsSearchChange={this.onItemsSearchChange}
              itemsSearchValue={this.state.itemsSearch}
              isItemsLoading={this.state.isItemsLoading}
              //items
              items={items}
              itemsTitle={'Eligible students'}
              itemsColumns={visibleItemsColumns}
              itemsSelected={itemsSelected}
              onItemClick={this.onItemClick}
              //items filters
              onItemsFilterChange={this.onItemsFilterChange}
              itemsFilters={itemsFilters}
              itemsFiltersOptions={itemsFiltersOptions}
              isShowItemsFilter={isShowItemsFilter}
              isItemsFiltered={isItemsFiltered}
              onItemsFilterClick={this.onItemsFilterClick}
              //accumulator
              itemsAccumulator={itemsAccumulator}
              itemsAccumulatorTitle={'Participants'}
              itemsAccumulatorColumns={visibleAccumulatorColumns}
              onItemAccumulatorClick={this.onItemAccumulatorClick}
              itemsAccumulatorSelected={itemsAccumulatorSelected}
              //accumulator filters
              onItemsAccumulatorFilterChange={this.onItemsAccumulatorFilterChange}
              itemsAccumulatorFilters={itemsAccumulatorFilters}
              itemsAccumulatorFiltersOptions={itemsAccumulatorFiltersOptions}
              isShowItemsAccumulatorFilter={isShowItemsAccumulatorFilter}
              isItemsAccumulatorFiltered={isItemsAccumulatorFiltered}
              onItemsAccumulatorFilterClick={this.onItemsAccumulatorFilterClick}
              //buttons
              onAddClick={this.onAddClick}
              onRemoveClick={this.onRemoveClick}
              //counts
              itemsCount={itemsCount}
              itemsAccumulatorCount={itemsAccumulatorCount}
              //current pages
              itemAccumulatorCurrentPage={1}
              itemCurrentPage={itemCurrentPage}
              itemsSetCurrentPageParams={this.itemsSetCurrentPageParams}
              //items filter buttons
              itemsOnApplyFilterClick={this.itemsOnApplyFilterClick}
              itemsOnClearFilterClick={this.itemsOnClearFilterClick}
              //items accumulator filter buttons
              itemsAccumulatorOnApplyFilterClick={this.itemsAccumulatorOnApplyFilterClick}
              itemsAccumulatorOnClearFilterClick={this.itemsAccumulatorOnClearFilterClick}
              //items select checkbox
              onSelectAllItemsClick={this.onSelectAllItemsClick}
              onSelectAllItemsOnPageClick={this.onSelectAllItemsOnPageClick}
              onUnselectAllItemsClick={this.onUnselectAllItemsClick}
              isSelectAllItemsChecked={isSelectAllItemsChecked}
              //items accumulator select checkbox
              onSelectAllItemsAccumulatorClick={this.onSelectAllItemsAccumulatorClick}
              onSelectAllItemsAccumulatorOnPageClick={this.onSelectAllItemsAccumulatorOnPageClick}
              onUnselectAllItemsAccumulatorClick={this.onUnselectAllItemsAccumulatorClick}
              isSelectAllItemsAccumulatorChecked={isSelectAllItemsAccumulatorChecked}
              //items sort
              sortItemsDirection={sortItemsDirection}
              sortItemsColumnsName={sortItemsColumnsName}
              onSortItemsClick={this.onSortItemsClick}
              //items accumulator sort
              sortItemsAccumulatorDirection={sortItemsAccumulatorDirection}
              sortItemsAccumulatorColumnsName={sortItemsAccumulatorColumnsName}
              onSortItemsAccumulatorClick={this.onSortItemsAccumulatorClick}
              itemsAddBuffer={itemsAddBuffer}
              itemsRemoveBuffer={itemsRemoveBuffer}
              onCellItemsClick={this.onClashClick}
              onCellItemsAccumulatorClick={this.onClashClick}
              //column visibility – items panel
              itemsAllColumns={allItemsColumns}
              itemsHiddenFields={itemsHiddenFields}
              onToggleItemsColumn={this.onToggleItemsColumn}
              isItemsColumnToggleable={this.isColumnToggleable}
              //column visibility – accumulator panel
              itemsAccumulatorAllColumns={allAccumulatorColumns}
              itemsAccumulatorHiddenFields={itemsAccumulatorHiddenFields}
              onToggleItemsAccumulatorColumn={this.onToggleItemsAccumulatorColumn}
              isItemsAccumulatorColumnToggleable={this.isColumnToggleable}
            />
          </div>
        </section>
        <div className="bSchoolTeamManager__footer">
          <div className="bSchoolTeamManager__footerLeft">
            {isShowFollowingEventsSection && (
              <SchoolEventClubEventRadioButtons onEditModeClick={this.onEditModeClick} editMode={editMode} />
            )}
          </div>

          <div className="bSchoolTeamManager__footerRight">
            <button type="button" className="bSchoolTeamManager__btnSecondary" onClick={this.goBack}>
              Cancel
            </button>
            <button type="button" className="bSchoolTeamManager__btnPrimary" onClick={this.onClickSave}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Save changes
            </button>
          </div>
        </div>
      </div>
    );
  }
}
